import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface ProjectCompetitor {
    name: string;
    url: string;
}

export interface IndustryItem {
    name: string;
    isPrimary: boolean;
}

export interface CategoryItem {
    name: string;
    isPrimary: boolean;
}

export interface Project {
    id: string;
    name: string;
    description: string;
    primary_country: string;
    language?: string;
    competitors?: ProjectCompetitor[];
    industry?: string;
    category?: string;
    industries?: IndustryItem[];
    categories?: CategoryItem[];
    /** Categories used for Oxylabs overview analysis (set at create/update; max matches project dialog) */
    analysis_categories?: string[];
    status: string;
    created_by: string;
    /** Display name of user who created the project (from API) */
    created_by_name?: string | null;
    created_at: string;
    updated_at: string;
    projectdata_count?: number;
}

export interface ProjectListResponse {
    projects: Project[];
    total: number;
    page: number;
    per_page: number;
}

export interface ProjectCountResponse {
    count: number;
}

export interface ProjectCreateRequest {
    name: string;
    description: string;
    primary_country: string;
    language?: string;
    competitors?: ProjectCompetitor[];
    industry?: string;
    category?: string;
    industries?: IndustryItem[];
    categories?: CategoryItem[];
    analysis_categories?: string[];
}

export interface SuggestBrandDetailsRequest {
    brand_name: string;
    brand_domain?: string;
    market?: string;
    language?: string;
}

export interface SuggestedCompetitor {
    name: string;
    url: string;
}

export interface SuggestBrandDetailsResponse {
    industries: string[];
    categories: string[];
    primary_industry?: string;
    primary_category?: string;
    competitors_high?: SuggestedCompetitor[];
    competitors_low?: SuggestedCompetitor[];
    cached?: boolean;
    error?: string;
}

@Injectable({
    providedIn: 'root'
})
export class ProjectManagementService {
    private readonly API_URL = environment.apiUrl;

    constructor(private http: HttpClient) { }

    // Create a new project/university
    createProject(projectRequest: ProjectCreateRequest): Observable<Project> {
        return this.http.post<Project>(`${this.API_URL}/projects/`, projectRequest);
    }

    /** Verify and explore: suggest industry and categories (web search + ChatGPT) before project creation */
    suggestBrandDetails(params: SuggestBrandDetailsRequest): Observable<SuggestBrandDetailsResponse> {
        return this.http.post<SuggestBrandDetailsResponse>(`${this.API_URL}/projects/suggest-brand-details`, params);
    }

    // Get all projects with access control
    getAllProjects(skip: number = 0, limit: number = 100): Observable<ProjectListResponse> {
        const params = new HttpParams()
            .set('skip', skip.toString())
            .set('limit', limit.toString());

        return this.http.get<ProjectListResponse>(`${this.API_URL}/projects/`, { params });
    }

    // Get project count for current authenticated user scope
    getProjectCount(): Observable<ProjectCountResponse> {
        return this.http.get<ProjectCountResponse>(`${this.API_URL}/projects/count`);
    }

    // Get only registered projects/universities for filter dropdowns
    getRegisteredProjects(): Observable<Project[]> {
        return this.http.get<Project[]>(`${this.API_URL}/projects/registered`);
    }

    // Get project by ID with access control
    getProjectById(projectId: string): Observable<Project> {
        return this.http.get<Project>(`${this.API_URL}/projects/${projectId}`);
    }

    // Delete all analysis versions for a project (before updating project details)
    deleteProjectAnalysisVersions(projectId: string): Observable<{ message: string; deleted_count: number }> {
        return this.http.delete<{ message: string; deleted_count: number }>(
            `${this.API_URL}/projects/${projectId}/analysis-versions`
        );
    }

    // Update project with access control
    updateProject(projectId: string, projectRequest: ProjectCreateRequest): Observable<Project> {
        return this.http.patch<Project>(`${this.API_URL}/projects/${projectId}`, projectRequest);
    }

    // Delete project with access control
    deleteProject(projectId: string): Observable<any> {
        return this.http.delete(`${this.API_URL}/projects/${projectId}`);
    }

    // Update project status (Admin only)
    updateProjectStatus(projectId: string, status: string): Observable<any> {
        const params = new HttpParams().set('status', status);
        return this.http.patch(`${this.API_URL}/projects/${projectId}/status`, {}, { params });
    }

    /** Admin only: set project owner (created_by) — same as scripts/reassign_project_owner.py */
    reassignProjectOwner(projectId: string, newOwnerId: string): Observable<Project> {
        return this.http.post<Project>(`${this.API_URL}/projects/${projectId}/reassign-owner`, {
            new_owner_id: newOwnerId,
        });
    }
}
